'use client';

import React, { useEffect, useRef } from 'react';
import { Hits, InfiniteHits, Pagination, useInfiniteHits } from 'react-instantsearch-hooks-web';

/**
 * Client-side product-listing loading styles, driven by the Contentstack
 * `section_with_product_listing.product_tiles_loading_style` field:
 *   - 'infinite'   → Show the first page, then auto-load the next batch as the user scrolls (default).
 *   - 'loadMore'   → Show the first page, then reveal the next batch on a "Load More" button click.
 *   - 'pagination' → One page of `hitsPerPage` tiles at a time with numbered pagination controls.
 * Any unexpected/empty CMS value safely falls back to 'infinite'.
 */
export type TileLoadingStyle = 'infinite' | 'loadMore' | 'pagination';

/** Map the raw CMS string (choices: "Infinite Scroll" | "Load More Button" | "Pagination") → style. */
export function mapTileLoadingStyle(raw?: string): TileLoadingStyle {
  const value = (raw ?? '').trim().toLowerCase();
  if (value === 'pagination') return 'pagination';
  if (value === 'load more button') return 'loadMore';
  return 'infinite'; // "Infinite Scroll" (default) or anything unexpected
}

interface Props {
  loadingStyle: TileLoadingStyle;
  hitComponent: (props: { hit: any }) => JSX.Element;
  rootClassName?: string;
  listClassName?: string;
  /** "Load More" button label (default "Load More"). */
  loadMoreLabel?: string;
}

// Numbered pagination controls (methven black selected item, dimmed disabled).
const PAGINATION_CLASS_NAMES = {
  root: 'mt-32 flex w-full justify-center',
  list: 'flex flex-row items-center gap-8',
  item: 'border border-gray-300',
  selectedItem: '!border-[#090909] bg-[#090909] [&_a]:text-white',
  disabledItem: 'opacity-50',
  link: 'flex items-center justify-center px-14 py-8 cursor-pointer text-14',
} as const;

/** Infinite scroll + "Load More" both build on InfiniteHits (which accumulates pages via showMore). */
function InfiniteVariant({ loadingStyle, hitComponent, rootClassName, listClassName, loadMoreLabel }: Props) {
  const { showMore, isLastPage } = useInfiniteHits();
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  // Auto-reveal the next batch as the sentinel scrolls into view (infinite scroll only).
  useEffect(() => {
    if (loadingStyle !== 'infinite') return;
    const el = sentinelRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !isLastPage) showMore();
      },
      { rootMargin: '200px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loadingStyle, isLastPage, showMore]);

  return (
    <>
      <InfiniteHits
        showPrevious={false}
        hitComponent={hitComponent}
        // The built-in "Show More" button is always hidden; we drive loading via the sentinel
        // (infinite) or our own styled button (loadMore).
        classNames={{ root: rootClassName, list: listClassName, loadMore: '!hidden' }}
      />
      {loadingStyle === 'infinite' && <div ref={sentinelRef} className="h-10" />}
      {loadingStyle === 'loadMore' && !isLastPage && (
        <div className="mt-32 flex w-full justify-center">
          <button
            type="button"
            onClick={() => showMore()}
            className="cursor-pointer border border-[#090909] px-32 py-12 text-16 font-medium text-[#090909] transition-colors hover:bg-[#090909] hover:text-white"
          >
            {loadMoreLabel ?? 'Load More'}
          </button>
        </div>
      )}
    </>
  );
}

/** Pagination shows one page at a time — Hits (current page) + numbered Pagination controls. */
function PaginatedVariant({ hitComponent, rootClassName, listClassName }: Props) {
  return (
    <>
      <Hits hitComponent={hitComponent} classNames={{ root: rootClassName, list: listClassName }} />
      <Pagination padding={2} classNames={PAGINATION_CLASS_NAMES} />
    </>
  );
}

/**
 * Renders the product tiles using the CMS-selected loading style. `pagination` and the
 * infinite/loadMore variants use different InstantSearch connectors, so they live in separate
 * sibling components (React hooks can't be called conditionally within one component).
 */
export default function ProductHits(props: Props) {
  return props.loadingStyle === 'pagination' ? <PaginatedVariant {...props} /> : <InfiniteVariant {...props} />;
}
